import { useTranslation } from 'react-i18next';
import type { MetricsChurn } from '@/services/adminMetricsService';
import { cn } from '@/lib/utils';

interface ChurnBlockProps {
  data: MetricsChurn;
  /** past_due count from summary */
  atRiskCount: number;
  formatCents: (cents: number, currency: 'BRL' | 'EUR') => string;
}

/**
 * Churn & retention summary: logo churn %, MRR churned, at-risk count.
 * Net MRR delta coloured by sign (success = positive, destructive = negative).
 */
export function ChurnBlock({ data, atRiskCount, formatCents }: ChurnBlockProps) {
  const { t } = useTranslation();

  const netBrl = data.mrr_net_cents.BRL;
  const netEur = data.mrr_net_cents.EUR;
  const netPositive = netBrl >= 0;

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-4">
      <div>
        <dt className="text-muted-foreground">{t('admin.metrics.churn.logo_pct')}</dt>
        <dd
          data-testid="churn-logo-pct"
          className={cn(
            'mt-1 text-xl font-bold tabular-nums',
            data.logo_churn_pct > 10 ? 'text-destructive' : 'text-foreground'
          )}
        >
          {data.logo_churn_pct}%
        </dd>
      </div>

      <div>
        <dt className="text-muted-foreground">{t('admin.metrics.churn.mrr_churned')}</dt>
        <dd className="mt-1 font-semibold tabular-nums text-destructive">
          <div>{formatCents(data.mrr_churned_cents.BRL, 'BRL')}</div>
          <div className="text-xs">{formatCents(data.mrr_churned_cents.EUR, 'EUR')}</div>
        </dd>
      </div>

      <div>
        <dt className="text-muted-foreground">{t('admin.metrics.churn.at_risk')}</dt>
        <dd
          className={cn(
            'mt-1 text-xl font-bold tabular-nums',
            atRiskCount > 0 ? 'text-warning' : 'text-foreground'
          )}
        >
          {atRiskCount}
        </dd>
      </div>

      <div>
        <dt className="text-muted-foreground">{t('admin.metrics.churn.mrr_net')}</dt>
        <dd
          className={cn(
            'mt-1 font-semibold tabular-nums',
            netPositive ? 'text-success' : 'text-destructive'
          )}
        >
          <div>
            {netPositive ? '+' : ''}
            {formatCents(netBrl, 'BRL')}
          </div>
          <div className="text-xs">
            {netPositive ? '+' : ''}
            {formatCents(netEur, 'EUR')}
          </div>
        </dd>
      </div>
    </dl>
  );
}
